import axios, { type AxiosInstance } from 'axios';
import { bridgeAuthorizationForRequest } from '../commerce/ensureBridgeToken';
import { readGuestCartId } from '../commerce/guestCart';
import {
  GUEST_CART_PROXY_BASE_URL,
  guestCartIdIn,
  isGuestCartProxyRoute,
} from '../commerce/guestCartProxy';

const COMMERCE_GATEWAY_PROXY_BASE_URL = '/api/commerce-gateway';
const BRIDGE_AUTHORIZATION_HEADER = 'X-Authorization';
/** The gateway authenticates the caller from this before reading the bridge token. */
const USER_AUTHORIZATION_HEADER = 'Authorization';
const BRIDGE_ROUTE_PREFIX = '/commerce/bridge/';

/** Non-prod (staging/ephemeral/dev/test) all target staging USG. */
export const resolveUsgOrigin = (): string =>
  // Literal access required: Next.js only inlines NEXT_PUBLIC_* into the
  // client bundle for literal `process.env.NEXT_PUBLIC_*` reads.
  process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
    ? 'https://services.unity.com'
    : 'https://staging.services.unity.com';

export type CommerceClientTarget = 'usg-direct' | 'proxy';

/**
 * Default stays 'proxy': surfaces opt into usg-direct one at a time as their
 * bridge-token provisioning is wired up (PDP Buy Now first, CPS-7658), and
 * repointing an opted-in surface back at the proxy is the Phase 0 rollback
 * (TDD §9).
 */
const DEFAULT_COMMERCE_CLIENT_TARGET: CommerceClientTarget = 'proxy';

/** Only create and the guest's own cart: a signed-in session without a token must not borrow the service key. */
const isGuestCartCall = (method?: string, url?: string): boolean => {
  if (!isGuestCartProxyRoute(method, url)) return false;
  const cartId = guestCartIdIn(url);
  return cartId === 'create' || cartId === readGuestCartId();
};

const attachBridgeAuthorization = (client: AxiosInstance) => {
  client.interceptors.request.use(async (config) => {
    // Only bridge routes take X-Authorization; commerce-core issues
    // identity/account calls (/unity/...) through this same client, and
    // those need the proxy's Genesis Authorization + X-Client-ID.
    if (!config.url?.startsWith(BRIDGE_ROUTE_PREFIX)) {
      config.baseURL = COMMERCE_GATEWAY_PROXY_BASE_URL;
      return config;
    }
    // Resolved per request, so a token minted or renewed
    // mid-session is picked up: no mount gate ran for this one — a React Query
    // refetch on window focus can fire under a page left mounted past the
    // access token's lifetime — so it also enforces the token's subject and
    // renews from the refresh cookie.
    const auth = await bridgeAuthorizationForRequest(resolveUsgOrigin());
    // A signed-out visitor holds no credential: their cart calls go through
    // the server route that holds the guest cart key.
    if (auth.status === 'none' && isGuestCartCall(config.method, config.url)) {
      config.baseURL = GUEST_CART_PROXY_BASE_URL;
      return config;
    }
    // A credential existed and renewing it failed: the request would only
    // reach the gateway unauthenticated, so it is rejected before it is sent.
    if (auth.status === 'failed') {
      throw auth.error;
    }
    // A signed-out visitor has nothing to renew from and sends no header.
    if (auth.status === 'authorized') {
      config.headers.set(
        BRIDGE_AUTHORIZATION_HEADER,
        auth.headers.xAuthorization
      );
      config.headers.set(USER_AUTHORIZATION_HEADER, auth.headers.authorization);
    }
    return config;
  });
  client.interceptors.response.use(undefined, (error) => {
    // The axios rejection carries the request config; strip both bearers
    // before they reach logs/Sentry (same property bridgeToken.ts preserves
    // via BridgeTokenRequestError). `Authorization` carries the Unity ID JWT
    // and its user claims, so it matters at least as much as the bridge token.
    if (axios.isAxiosError(error)) {
      error.config?.headers?.delete(BRIDGE_AUTHORIZATION_HEADER);
      error.config?.headers?.delete(USER_AUTHORIZATION_HEADER);
    }
    return Promise.reject(error);
  });
};

/**
 * Browser-direct USG client: gateway rate limits key on the user's IP instead
 * of our few shared egress IPs. Even in usg-direct mode only /commerce/bridge/
 * routes go direct; everything else stays on the proxy, and a signed-out
 * visitor's cart calls go through the guest cart route.
 */
export const createCommerceAxiosClient = (
  target: CommerceClientTarget = DEFAULT_COMMERCE_CLIENT_TARGET,
  {
    timeout,
    readTimeout,
  }: {
    /**
     * Milliseconds before a request rejects with axios's response-less
     * timeout error, like any network failure. Unset waits forever (axios's
     * default).
     */
    timeout?: number;
    /** The same for GETs, which a caller may retry; unset uses `timeout`. */
    readTimeout?: number;
  } = {}
): AxiosInstance => {
  const client = axios.create({
    baseURL:
      target === 'proxy'
        ? COMMERCE_GATEWAY_PROXY_BASE_URL
        : `${resolveUsgOrigin()}/api`,
    headers: { 'Content-Type': 'application/json' },
    timeout,
  });
  if (target === 'usg-direct') {
    attachBridgeAuthorization(client);
  }
  if (readTimeout !== undefined) {
    client.interceptors.request.use((config) => {
      if (config.method === 'get') config.timeout = readTimeout;
      return config;
    });
  }
  return client;
};
